/**
 * Placement math for the single-furniture Duplicate flow (SRS REQ-368).
 *
 * Duplicate drops a copy on the SAME page as the source, offset to its right. All coordinates
 * here are in the page's RENDERED (rotation-applied) orientation — the same space as
 * `TFurniture.coordinates`, `getPageInfo` dims, and the duplicate endpoint body — so this is
 * plain axis-aligned geometry with no rotation handling (see AnnotationService for why).
 */

import type { TCoordinates } from "types/elevationReferences.type"

/**
 * How many slots each direction probes before cascading to the next. The copy is offset one
 * source-size per step, so this caps the search to a small band around the source (≈3 widths /
 * heights) — a duplicate never lands far away just because that whole side of the page is free.
 */
const MAX_STEPS_PER_DIRECTION = 3

/** Strict overlap — rectangles that merely share an edge are NOT considered overlapping. */
const rectsOverlap = (a: TCoordinates, b: TCoordinates): boolean =>
	a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0

/**
 * Compute the top-left for a duplicate of `source` on the same page.
 *
 * Placement is tried in four directions, radiating out from the source to keep the copy close by:
 *   1. Right — start one source-width to the right at the same Y, step right by the width.
 *   2. Down  — directly below the source (same X), step down by the height.
 *   3. Left  — one source-width to the left at the same Y, step left by the width.
 *   4. Up    — directly above the source (same X), step up by the height.
 * Each scan probes at most `MAX_STEPS_PER_DIRECTION` slots (and stops early at the page edge) and
 * returns the first slot free of every `occupied` rect, so a duplicate stays in a tight band
 * around the source instead of sliding to a far edge. If no direction has a free slot in range,
 * the copy is clamped flush against the bottom edge (accepting overlap) so the user always gets a
 * duplicate to drag into place.
 *
 * `occupied` should be the other furniture rects on the source's page (exclude the source).
 * Returns only `{ x0, y0 }`; the backend derives `x1`/`y1` from the source's size.
 */
export const computeDuplicatePlacement = (
	source: TCoordinates,
	occupied: TCoordinates[],
	pageWidth: number,
	pageHeight: number,
): { x0: number; y0: number } => {
	const width = source.x1 - source.x0
	const height = source.y1 - source.y0
	const free = (x0: number, y0: number): boolean =>
		!occupied.some((rect) => rectsOverlap({ x0, y0, x1: x0 + width, y1: y0 + height }, rect))

	// The width/height guards below skip a scan for a degenerate (zero/negative) size: without a
	// positive step the loop can't advance (`x0 += 0`) and would spin forever.

	// 1. Right — tile along the source's row toward the right edge (bounded band).
	if (width > 0) {
		let x0 = source.x0 + width
		for (let step = 0; step < MAX_STEPS_PER_DIRECTION && x0 + width <= pageWidth; step++, x0 += width) {
			if (free(x0, source.y0)) return { x0, y0: source.y0 }
		}
	}

	// 2. Down — tile down the source's column toward the bottom edge (bounded band).
	if (height > 0) {
		let y0 = source.y0 + height
		for (let step = 0; step < MAX_STEPS_PER_DIRECTION && y0 + height <= pageHeight; step++, y0 += height) {
			if (free(source.x0, y0)) return { x0: source.x0, y0 }
		}
	}

	// 3. Left — tile along the source's row back toward the left edge (bounded band).
	if (width > 0) {
		let x0 = source.x0 - width
		for (let step = 0; step < MAX_STEPS_PER_DIRECTION && x0 >= 0; step++, x0 -= width) {
			if (free(x0, source.y0)) return { x0, y0: source.y0 }
		}
	}

	// 4. Up — tile up the source's column toward the top edge (bounded band).
	if (height > 0) {
		let y0 = source.y0 - height
		for (let step = 0; step < MAX_STEPS_PER_DIRECTION && y0 >= 0; step++, y0 -= height) {
			if (free(source.x0, y0)) return { x0: source.x0, y0 }
		}
	}

	// 5. No free slot within range in any direction (or a degenerate-size source). Park the copy
	// flush against the bottom edge, accepting overlap so the user always gets a duplicate.
	//
	// The backend still rejects a copy whose FULL coordinates exactly match an existing object
	// (the source, or any same-size furniture already sitting at the clamp position). Overlap is
	// fine — only an exact match is rejected — so nudge the top-left up one unit at a time until
	// it is distinct, stopping at the top edge (the best we can do on a completely packed page).
	const isExactDuplicate = (x0: number, y0: number): boolean =>
		(x0 === source.x0 && y0 === source.y0) ||
		occupied.some((r) => r.x0 === x0 && r.y0 === y0 && r.x1 === x0 + width && r.y1 === y0 + height)

	let y0 = Math.max(0, pageHeight - height)
	while (y0 > 0 && isExactDuplicate(source.x0, y0)) y0 -= 1
	return { x0: source.x0, y0 }
}
